import {
  collection,
  createDocument,
  EditorState,
  EditorView,
  store as editorStore,
  markdownLoader,
  type NodeViewConstructor,
  Plugin,
  type PMNode,
  resolve,
  Schema,
  setupHistoryFor,
  stripSyntheticSuggestionText,
} from '@bangle.io/prosemirror-plugins';
import type { TextStats } from '@bangle.io/service-core';
import type { Store } from '@bangle.io/types';
import {
  prosemirrorToYXmlFragment,
  redoCommand,
  undoCommand,
  yCursorPlugin,
  ySyncPlugin,
  yUndoPlugin,
  yXmlFragmentToProsemirror,
} from 'y-prosemirror';
import type { Awareness } from 'y-protocols/awareness';
import * as Y from 'yjs';

import { createMarkdownClipboardPluginProps } from './clipboard-markdown';
import { shouldPreferMarkdownOverYjs } from './collab-content-reconcile';
import { remoteCursorBuilder, remoteSelectionBuilder } from './collab-cursor';
import { createDocStatsPlugin } from './doc-stats-plugin';
import type { setupExtensions } from './extensions';

/**
 * Produces the exact Markdown projection used for durable editor saves.
 * Reconciliation must use this same projection when deciding whether disk
 * already contains the current document, including removal of UI-only
 * suggestion text that must never reach storage.
 */
export function serializeEditorDocumentForSave(
  markdown: ReturnType<typeof markdownLoader>,
  doc: PMNode,
): string {
  return markdown.serializer.serialize(stripSyntheticSuggestionText(doc));
}

export type CollabEditorOptions = {
  fragment: Y.XmlFragment;
  awareness: Awareness;
  readOnly?: boolean;
  /** `workspace_files.mtime` for the note being opened. */
  markdownMtimeMs?: number | null;
  /** Latest durable Yjs activity from Postgres. */
  yjsUpdatedAtMs?: number | null;
  /** Local Yjs has unsaved edits that must not be clobbered. */
  yjsDirty?: boolean;
};

function seedYXmlFragmentFromPmDoc(
  fragment: Y.XmlFragment,
  pmDoc: PMNode,
  forceReplace: boolean,
) {
  const ydoc = fragment.doc;
  const seed = () => {
    if (forceReplace && fragment.length > 0) {
      fragment.delete(0, fragment.length);
    }
    if (fragment.length === 0) {
      prosemirrorToYXmlFragment(pmDoc, fragment);
    }
  };
  if (ydoc) {
    Y.transact(ydoc, seed);
  } else {
    seed();
  }
}

export function createEditor({
  domNode,
  defaultContent = '',
  onDocChange,
  onStatsChange,
  store,
  extensions,
  nodeViews,
  collab,
  readOnly = false,
}: {
  domNode: HTMLElement;
  defaultContent?: string;
  onDocChange?: (doc: string) => void;
  onStatsChange?: (stats: TextStats) => void;
  store: Store;
  extensions: ReturnType<typeof setupExtensions>;
  nodeViews?: Record<string, NodeViewConstructor>;
  collab?: CollabEditorOptions;
  /** View-only surface (e.g. shared viewer). Selection/copy still work. */
  readOnly?: boolean;
}) {
  // Collaborative notes undo through Yjs: prosemirror-history and its keymap
  // are replaced rather than supplemented — two independent undo stacks over
  // one document would each be blind to the other's edits. The chords survive
  // the swap, bound to Yjs' UndoManager instead.
  const extensionsForEditor = collab
    ? {
        ...extensions,
        history: setupHistoryFor({
          undo: undoCommand,
          redo: redoCommand,
        }),
      }
    : extensions;

  const resolved = resolve(
    {
      editor: collection({
        id: 'editor',
        plugin: {
          storePlugin: editorStore.storePlugin(store),
        },
      }),
      ...extensionsForEditor,
      saveDoc: collection({
        id: 'save-doc',
        plugin: {
          // Ctrl+C/X must put Markdown on the clipboard without ProseMirror's
          // competing text/html. Destinations prefer HTML, so shipping both
          // made "copy as Markdown" paste as rich text everywhere it mattered.
          saveDoc: new Plugin({
            props: createMarkdownClipboardPluginProps({
              serialize: (doc) => markdown.serializer.serialize(doc),
              parse: (source) => markdown.parser.parse(source),
            }),
            view() {
              return {
                update(view, prevState) {
                  if (onDocChange && !view.state.doc.eq(prevState.doc)) {
                    const result = serializeEditorDocumentForSave(
                      markdown,
                      view.state.doc,
                    );

                    onDocChange(result);
                  }
                },
              };
            },
          }),
        },
      }),
    },
    false,
    true,
  );

  const schema = new Schema({
    topNode: 'doc',
    nodes: resolved.nodes,
    marks: resolved.marks,
  });

  const markdown = markdownLoader(
    [...Object.values(extensionsForEditor)],
    schema,
  );

  const parsedDoc = defaultContent
    ? markdown.parser.parse(defaultContent)
    : createDocument({ schema, content: '' });

  let doc = parsedDoc;
  if (collab) {
    let seedFromMarkdown = collab.fragment.length === 0;
    if (!seedFromMarkdown && parsedDoc && !collab.readOnly) {
      try {
        const yjsDoc = yXmlFragmentToProsemirror(schema, collab.fragment);
        const yjsMarkdown = serializeEditorDocumentForSave(markdown, yjsDoc);
        seedFromMarkdown = shouldPreferMarkdownOverYjs({
          markdown: defaultContent,
          yjsMarkdown,
          markdownMtimeMs: collab.markdownMtimeMs ?? null,
          yjsUpdatedAtMs: collab.yjsUpdatedAtMs ?? null,
          yjsDirty: collab.yjsDirty ?? false,
        });
      } catch {
        // Corrupt or schema-mismatched Yjs — recover from Markdown.
        seedFromMarkdown = Boolean(defaultContent);
      }
    }
    if (seedFromMarkdown && parsedDoc) {
      seedYXmlFragmentFromPmDoc(collab.fragment, parsedDoc, true);
    }
    try {
      doc = yXmlFragmentToProsemirror(schema, collab.fragment);
    } catch {
      doc = parsedDoc;
    }
  }

  const basePlugins = resolved.resolvePlugins({ schema });
  const withStats = onStatsChange
    ? [...basePlugins, createDocStatsPlugin(onStatsChange)]
    : basePlugins;
  const plugins = collab
    ? [
        ySyncPlugin(collab.fragment),
        // Custom builders render a remote caret as a zero-footprint overlay
        // (thin blinking line + floating name label) instead of the upstream
        // in-flow email blob; see `collab-cursor.ts` and `collab-cursor.css`.
        yCursorPlugin(collab.awareness, {
          cursorBuilder: remoteCursorBuilder,
          selectionBuilder: remoteSelectionBuilder,
        }),
        yUndoPlugin(),
        ...withStats,
      ]
    : withStats;

  return new EditorView(
    { mount: domNode },
    {
      state: EditorState.create({
        doc,
        schema,
        plugins,
      }),
      nodeViews,
      editable: () => !(readOnly || collab?.readOnly),
    },
  );
}
